/**
 * navigationGuard.ts — Singleton global para bloquear navegación condicionalmente.
 *
 * QUÉ HACE:
 * - Provee un punto de registro único (register/clear) para una condición de bloqueo
 * - El Sidebar comprueba `check()` antes de navegar — si la condición es true, bloquea
 * - Cuando bloquea, actualiza el navigationGuardStore (Zustand) para mostrar el modal
 * - Compatible con BrowserRouter (no requiere createBrowserRouter / data router)
 *
 * QUÉ EXPORTA: navigationGuard (objeto)
 *
 * QUÉ NO DEBE HACER:
 * - No debe renderizar UI (eso lo hace MainLayout)
 * - No debe gestionar estado de React directamente
 */

import { useNavigationGuardStore } from "@/store/navigationGuardStore";

let _condition: (() => boolean) | null = null;

export const navigationGuard = {
  /**
   * Registra la condición de bloqueo.
   * @param condition  Devuelve true si se debe bloquear la navegación
   */
  register(condition: () => boolean): void {
    _condition = condition;
  },

  /** Elimina la guardia activa */
  clear(): void {
    _condition = null;
  },

  /**
   * Comprueba si se debe bloquear la navegación.
   * - Si NO hay guardia activa o la condición es false  → devuelve true (navegar libremente)
   * - Si la condición es true (hay datos en riesgo)     → activa el store y devuelve false
   *
   * @param proceed  Función que ejecuta la navegación si el usuario confirma
   * @returns true = permitir, false = bloqueado
   */
  check(proceed: () => void): boolean {
    if (_condition?.()) {
      useNavigationGuardStore.getState().block(proceed);
      return false;
    }
    proceed();
    return true;
  },
};
